// src/components/RPA/buttons/GenerateAuditorsReportButton.js
//
// Auditor's Report button + modal. Same shape as GenerateWorkpaperButton:
// a small trigger in the document row that opens a full modal.
//
// Three tabs:
//   📄 Report          — header, opinion assessment, narrative sections
//   ⚠️  Issues Register — every issue with its CURRENT status, filterable
//   🛡  Internal Control — AU-C 265 buckets (MW / SD / compliance / other)
//
// The register is rebuilt server-side on every open, so what shows here is the
// live state of the AuditIssue collection, not a cached package. The staleness
// banner fires when the register moved after the last issued snapshot.
//
// Props: docId (required), onDone (optional callback, fired on open and after
//        the report is issued so the parent can refresh its row).

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API || 'http://localhost:8000';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS (mirrors models/AuditIssue.js enums)
// ─────────────────────────────────────────────────────────────────────────────
const ISSUE_TYPE_LABELS = {
  material_weakness: 'Material Weakness',
  significant_deficiency: 'Significant Deficiency',
  finding: 'Finding',
  observation: 'Observation',
  control_deficiency: 'Control Deficiency',
  compliance_violation: 'Compliance Violation',
  other: 'Other',
};

const SEVERITY_COLORS = {
  critical: '#991b1b',
  high: '#b45309',
  moderate: '#1e40af',
  low: '#374151',
};

const STATUS_COLORS = {
  open: '#dc2626',
  in_remediation: '#d97706',
  pending_review: '#2563eb',
  closed: '#059669',
  escalated: '#7c3aed',
  voided: '#9ca3af',
};

const VERIFICATION_LABELS = {
  not_started: 'Not started',
  pending: 'Pending',
  verified: 'Verified',
  rejected: 'Rejected',
};

const RECOMMENDATION_LABELS = {
  unmodified: 'No open conditions bear on the opinion',
  unmodified_with_governance_communication:
    'Written communication to those charged with governance required',
  modification_consideration_required: 'Partner must assess whether the opinion is modified',
};

// AU-C 700 opinion type — mirrors OPINION_LABELS in the controller. Used only
// to badge report.opinion_assessment.effective_type; the actual paragraph
// text is generated server-side.
const OPINION_TYPE_LABELS = {
  unmodified: 'Unmodified Opinion',
  qualified: 'Qualified Opinion',
  adverse: 'Adverse Opinion',
  disclaimer: 'Disclaimer of Opinion',
};

// ─────────────────────────────────────────────────────────────────────────────
// STYLE TOKENS (matched to GenerateWorkpaperButton so the two modals feel like
// one product; teal accent instead of purple to distinguish the report)
// ─────────────────────────────────────────────────────────────────────────────
const s = {
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
    zIndex: 9999, display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
    overflowY: 'auto', padding: '24px 16px',
  },
  modal: {
    background: '#fff', borderRadius: 12, width: '100%', maxWidth: 1040,
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)', position: 'relative',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '18px 24px 0', borderBottom: '1px solid #e5e7eb',
    position: 'sticky', top: 0, background: '#fff', zIndex: 10, borderRadius: '12px 12px 0 0',
  },
  body: { padding: '16px 24px 32px' },
  triggerBtn: {
    background: '#0f766e', color: '#fff', border: 'none',
    padding: '4px 10px', borderRadius: 4, fontSize: 11, cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: 4,
  },
  closeBtn: {
    background: 'none', border: '1px solid #e5e7eb', borderRadius: 6,
    padding: '5px 12px', cursor: 'pointer', fontSize: 13, color: '#374151',
  },
  refreshBtn: {
    background: 'none', border: '1px solid #0f766e', color: '#0f766e',
    padding: '4px 8px', borderRadius: 4, fontSize: 11, cursor: 'pointer',
  },
  tab: (active) => ({
    padding: '8px 16px', marginRight: 4, cursor: 'pointer', fontSize: 13, fontWeight: 500,
    color: active ? '#0f766e' : '#6b7280', background: 'none',
    border: 'none', borderBottom: active ? '2px solid #0f766e' : '2px solid transparent',
  }),
  summaryGrid: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 10, marginBottom: 14,
  },
  summaryTile: {
    border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px', textAlign: 'center',
  },
  badge: (color) => ({
    display: 'inline-block', padding: '2px 7px', borderRadius: 999, fontSize: 10, fontWeight: 600,
    background: color === 'green' ? '#d1fae5' : color === 'red' ? '#fee2e2' : color === 'yellow' ? '#fef3c7' : color === 'blue' ? '#dbeafe' : color === 'teal' ? '#ccfbf1' : '#f3f4f6',
    border: `1px solid ${color === 'green' ? '#22c55e' : color === 'red' ? '#ef4444' : color === 'yellow' ? '#f59e0b' : color === 'blue' ? '#3b82f6' : color === 'teal' ? '#14b8a6' : '#d1d5db'}`,
    color: color === 'green' ? '#065f46' : color === 'red' ? '#991b1b' : color === 'yellow' ? '#92400e' : color === 'blue' ? '#1e40af' : color === 'teal' ? '#115e59' : '#374151',
  }),
  sectionCard: { border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: 10, overflow: 'hidden' },
  sectionHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '9px 14px', cursor: 'pointer', userSelect: 'none', background: '#f9fafb',
  },
  sectionBody: { padding: '12px 16px', borderTop: '1px solid #e5e7eb' },
  actionBtn: (color) => ({
    background: color || '#0f766e', color: '#fff', border: 'none',
    padding: '5px 12px', borderRadius: 5, fontSize: 11, cursor: 'pointer', fontWeight: 500,
  }),
  ghostBtn: {
    background: 'none', border: '1px solid #d1d5db', color: '#374151',
    padding: '5px 12px', borderRadius: 5, fontSize: 11, cursor: 'pointer',
  },
  select: {
    border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 10px',
    fontSize: 11, background: '#fff', cursor: 'pointer',
  },
  th: {
    textAlign: 'left', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4,
    color: '#6b7280', fontWeight: 600, padding: '7px 8px', borderBottom: '1px solid #e5e7eb',
    background: '#f9fafb', position: 'sticky', top: 0,
  },
  td: { fontSize: 11, color: '#374151', padding: '8px', borderBottom: '1px solid #f3f4f6', verticalAlign: 'top' },
  banner: (tone) => ({
    borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 12,
    background: tone === 'red' ? '#fef2f2' : tone === 'yellow' ? '#fffbeb' : '#f0fdfa',
    border: `1px solid ${tone === 'red' ? '#fca5a5' : tone === 'yellow' ? '#fcd34d' : '#5eead4'}`,
    color: tone === 'red' ? '#991b1b' : tone === 'yellow' ? '#92400e' : '#115e59',
  }),
  textarea: {
    width: '100%', border: '1px solid #d1d5db', borderRadius: 6, padding: '8px 10px',
    fontSize: 12, resize: 'vertical', minHeight: 60, boxSizing: 'border-box',
    fontFamily: 'inherit', lineHeight: 1.5,
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────
const fmtDate = (iso) => iso
  ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '—';

const fmtDay = (iso) => iso
  ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  : '—';

const escapeHtml = (str) => String(str ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

// ─────────────────────────────────────────────────────────────────────────────
// REMEDIATION STAGE PIPS — four dots matching the ClientsPanel tracker
// ─────────────────────────────────────────────────────────────────────────────
const StagePips = ({ stage }) => {
  const steps = [
    { on: stage?.hasResponse, label: 'Management response' },
    { on: stage?.hasPlan, label: 'Remediation plan' },
    { on: stage?.asserted, label: 'Completion asserted' },
    { on: stage?.verified, label: 'Auditor verified' },
  ];
  return (
    <span style={{ display: 'inline-flex', gap: 3 }} title={steps.map(x => `${x.on ? '✓' : '○'} ${x.label}`).join('\n')}>
      {steps.map((x, i) => (
        <span key={i} style={{
          width: 8, height: 8, borderRadius: 999,
          background: x.on ? '#0f766e' : '#e5e7eb',
          border: `1px solid ${x.on ? '#0f766e' : '#d1d5db'}`,
        }} />
      ))}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ISSUE ROW — one line of the register, expandable
// ─────────────────────────────────────────────────────────────────────────────
const RegisterRow = ({ row }) => {
  const [open, setOpen] = useState(false);
  const statusColor = STATUS_COLORS[row.status] || '#6b7280';

  return (
    <>
      <tr onClick={() => setOpen(o => !o)} style={{ cursor: 'pointer', background: row.is_overdue ? '#fffbf5' : 'transparent' }}>
        <td style={{ ...s.td, fontWeight: 700, color: '#9ca3af', width: 44 }}>#{row.issue_number ?? '—'}</td>
        <td style={{ ...s.td, fontWeight: 600, color: '#111827' }}>
          {row.title}
          {row.carried_forward_from && (
            <span style={{ ...s.badge('yellow'), marginLeft: 6 }}>
              carried from #{row.carried_forward_from.issue_number}
            </span>
          )}
        </td>
        <td style={s.td}>{ISSUE_TYPE_LABELS[row.type] || row.type}</td>
        <td style={{ ...s.td, color: SEVERITY_COLORS[row.severity], fontWeight: 600 }}>{row.severity}</td>
        <td style={s.td}>
          <span style={{
            ...s.badge('grey'),
            background: `${statusColor}18`,
            border: `1px solid ${statusColor}40`,
            color: statusColor,
          }}>
            {row.status_label}
          </span>
        </td>
        <td style={s.td}><StagePips stage={row.remediation_stage} /></td>
        <td style={s.td}>{VERIFICATION_LABELS[row.verification_status] || row.verification_status}</td>
        <td style={s.td}>
          {row.due_date ? fmtDay(row.due_date) : '—'}
          {row.is_overdue && <div style={{ color: '#b45309', fontWeight: 600 }}>{row.days_overdue}d overdue</div>}
        </td>
        <td style={{ ...s.td, textAlign: 'right' }}>{row.amount_display || '—'}</td>
        <td style={{ ...s.td, width: 20, color: '#9ca3af' }}>{open ? '▲' : '▼'}</td>
      </tr>
      {open && (
        <tr>
          <td colSpan={10} style={{ ...s.td, background: '#f9fafb', padding: '12px 16px' }}>
            {row.description && (
              <div style={{ marginBottom: 10, lineHeight: 1.6 }}>
                <strong style={{ color: '#111827' }}>Condition</strong>
                <div style={{ marginTop: 3 }}>{row.description}</div>
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <strong style={{ color: '#111827' }}>Management response</strong>
                <div style={{ marginTop: 3, lineHeight: 1.6 }}>
                  {row.management_response || 'Not received.'}
                  {row.management_responded_at && (
                    <div style={{ color: '#6b7280', marginTop: 3 }}>
                      {row.management_responded_by || 'Management'} · {fmtDate(row.management_responded_at)}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <strong style={{ color: '#111827' }}>Remediation plan</strong>
                <div style={{ marginTop: 3, lineHeight: 1.6 }}>
                  {row.remediation_plan || 'Not recorded.'}
                  {row.remediation_target_date && (
                    <div style={{ color: '#6b7280', marginTop: 3 }}>Target {fmtDay(row.remediation_target_date)}</div>
                  )}
                  {row.remediation_asserted_at && (
                    <div style={{ color: '#6b7280' }}>
                      Completion asserted by {row.remediation_asserted_by || 'management'} · {fmtDate(row.remediation_asserted_at)}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {row.remediation_actions?.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <strong style={{ color: '#111827' }}>Corrective actions ({row.remediation_actions.length})</strong>
                {row.remediation_actions.map((a, i) => (
                  <div key={i} style={{ marginTop: 5, padding: '6px 10px', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 5 }}>
                    <div>{a.description}</div>
                    <div style={{ color: '#6b7280', marginTop: 2 }}>
                      {a.owner || 'Unassigned'} · {a.status?.replace(/_/g, ' ')}
                      {a.target_date ? ` · target ${fmtDay(a.target_date)}` : ''}
                      {a.evidence_ref ? ` · evidence ${a.evidence_ref}` : ''}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid #e5e7eb', color: '#6b7280', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <span>Raised by {row.raised_by || 'unknown'} · {fmtDay(row.raised_at)}</span>
              {row.owner && <span>Owner: {row.owner}</span>}
              {row.workpaper_ref && <span>Workpaper {row.workpaper_ref}</span>}
              {row.verified_at && <span>Verified by {row.verified_by} · {fmtDay(row.verified_at)}</span>}
              {row.rejection_count > 0 && <span style={{ color: '#b45309' }}>Assertion rejected {row.rejection_count}x</span>}
              {row.closed_at && <span>Closed {fmtDay(row.closed_at)} by {row.closed_by || 'unknown'}</span>}
              {row.closed_without_verification && <span style={{ color: '#991b1b', fontWeight: 600 }}>Closed without verification</span>}
              <span>Status as of {fmtDate(row.status_as_of)}</span>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// PRINTABLE HTML — opens in a new window and calls print()
// ─────────────────────────────────────────────────────────────────────────────
function printReport(report) {
  const h = report.report_header || {};
  const sum = report.issue_summary || {};
  const rows = report.issues_register || [];

  const registerRows = rows.map(r => `
    <tr>
      <td>${escapeHtml(r.issue_number)}</td>
      <td>${escapeHtml(r.title)}</td>
      <td>${escapeHtml(r.type_label)}</td>
      <td>${escapeHtml(r.severity)}</td>
      <td><strong>${escapeHtml(r.status_label)}</strong></td>
      <td>${escapeHtml(VERIFICATION_LABELS[r.verification_status] || r.verification_status)}</td>
      <td>${r.due_date ? escapeHtml(fmtDay(r.due_date)) : ''}</td>
      <td style="text-align:right">${escapeHtml(r.amount_display || '')}</td>
    </tr>`).join('');

  const narrative = (report.narrative_sections || []).map(sec => `
    ${sec.section_id === 'AR-100' ? '<div class="report-break"><span>Report on Internal Control and Compliance follows</span></div>' : ''}
    <section>
      <h2>${escapeHtml(sec.section_id)} · ${escapeHtml(sec.title)}</h2>
      <pre>${escapeHtml(sec.body)}</pre>
    </section>`).join('');

  const html = `<!doctype html>
<html><head><meta charset="utf-8"><title>${escapeHtml(h.report_id || 'Auditor Report')}</title>
<style>
  body { font-family: Georgia, 'Times New Roman', serif; color:#111827; max-width:800px; margin:40px auto; line-height:1.6; }
  h1 { font-size:22px; margin-bottom:2px; }
  h2 { font-size:14px; text-transform:uppercase; letter-spacing:.06em; border-bottom:1px solid #111827; padding-bottom:4px; margin-top:28px; }
  .meta { color:#4b5563; font-size:13px; margin-bottom:24px; }
  pre { font-family:inherit; white-space:pre-wrap; font-size:13px; margin:8px 0; }
  table { width:100%; border-collapse:collapse; font-size:11px; font-family:Arial,Helvetica,sans-serif; margin-top:8px; }
  th { text-align:left; border-bottom:1.5px solid #111827; padding:5px 4px; font-size:10px; text-transform:uppercase; }
  td { border-bottom:1px solid #e5e7eb; padding:5px 4px; vertical-align:top; }
  .foot { margin-top:36px; padding-top:10px; border-top:1px solid #d1d5db; font-size:10px; color:#6b7280; }
  .report-break { page-break-before:always; margin:32px 0 4px; padding-top:14px; border-top:2px solid #111827; text-align:center; font-size:10px; text-transform:uppercase; letter-spacing:.08em; color:#6b7280; font-style:italic; }
  @media print { body { margin:0; } }
</style></head><body>
  <div class="meta">
    Report ${escapeHtml(h.report_id)} · Prepared ${escapeHtml(fmtDate(h.generated_at))} by ${escapeHtml(h.generated_by || 'unknown')}
    ${report.issuance?.issued ? `<br><strong>Issued ${escapeHtml(fmtDate(report.issuance.issued_at))} by ${escapeHtml(report.issuance.issued_by?.name || '')}</strong>` : '<br><em>Draft — not yet issued</em>'}
  </div>
  ${narrative}
  <section>
    <h2>Issues Register (${rows.length})</h2>
    <table>
      <thead><tr><th>#</th><th>Issue</th><th>Type</th><th>Severity</th><th>Status</th><th>Verification</th><th>Due</th><th>Amount</th></tr></thead>
      <tbody>${registerRows || '<tr><td colspan="8">No issues raised.</td></tr>'}</tbody>
    </table>
  </section>
  <div class="foot">
    Open ${escapeHtml(sum.open)} of ${escapeHtml(sum.total)} · Unremediated exposure ${escapeHtml(sum.unremediated_amount_display)} ·
    Register read live at ${escapeHtml(fmtDate(h.generated_at))} ·
    Generated by RPA Automated Audit System (Azure Government)
  </div>
</body></html>`;

  const w = window.open('', '_blank');
  if (!w) {
    alert('Allow pop-ups for this site to print the report.');
    return;
  }
  w.document.write(html);
  w.document.close();
  w.focus();
  setTimeout(() => w.print(), 400);
}

// ─────────────────────────────────────────────────────────────────────────────
// MODAL
// ─────────────────────────────────────────────────────────────────────────────
const AuditorsReportModal = ({ docId, onClose, onIssued }) => {
  const [tab, setTab] = useState('report');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [showIssueForm, setShowIssueForm] = useState(false);
  const [issueNote, setIssueNote] = useState('');
  const [issuing, setIssuing] = useState(false);
  // AU-C 700 opinion the partner confirms at issuance. The controller keys
  // AR-020 off issuance.opinion_confirmed; leaving this unset makes an issued
  // report render its opinion paragraph as a draft.
  const [issueOpinion, setIssueOpinion] = useState('');

  const load = useCallback(async (save = true) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(
        `${API_URL}/admin/rpa/docs/${docId}/auditors-report${save ? '' : '?save=false'}`,
        { withCredentials: true }
      );
      const built = res.data?.auditors_report || null;
      setReport(built);
      // effective_type is already normalized server-side, so it is the only
      // safe seed. carried_opinion is a Mixed field and may not normalize.
      setIssueOpinion(prev => prev || built?.opinion_assessment?.effective_type || '');
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not build the report. Check that findings have been generated for this document.');
    } finally {
      setLoading(false);
    }
  }, [docId]);

  useEffect(() => { load(true); }, [load]);

  const submitIssue = async (acknowledgeUnverified = false) => {
    setIssuing(true);
    setError('');
    try {
      const res = await axios.post(
        `${API_URL}/admin/rpa/docs/${docId}/auditors-report/issue`,
        { note: issueNote, opinion: issueOpinion, acknowledgeUnverified },
        { withCredentials: true }
      );
      setReport(res.data?.auditors_report || null);
      setShowIssueForm(false);
      setIssueNote('');
      if (onIssued) onIssued();
    } catch (e) {
      if (e?.response?.status === 409) {
        const n = e.response.data?.awaiting_verification || 0;
        if (window.confirm(`${n} issues are awaiting auditor verification. Issue the report anyway?`)) {
          setIssuing(false);
          return submitIssue(true);
        }
      } else {
        setError(e?.response?.data?.message || 'Could not issue the report.');
      }
    } finally {
      setIssuing(false);
    }
  };

  // ── Derived ──────────────────────────────────────────────────────────────
  const header = report?.report_header || {};
  const summary = report?.issue_summary || {};
  const opinion = report?.opinion_assessment || {};
  const controls = report?.internal_control_report || {};
  const integrity = report?.data_integrity || {};
  const issuance = report?.issuance || {};

  const filtered = useMemo(() => {
    let rows = report?.issues_register || [];
    if (filterStatus === 'open') rows = rows.filter(r => r.is_open);
    else if (filterStatus === 'overdue') rows = rows.filter(r => r.is_overdue);
    else if (filterStatus) rows = rows.filter(r => r.status === filterStatus);
    if (filterType) rows = rows.filter(r => r.type === filterType);
    return rows;
  }, [report, filterStatus, filterType]);

  // The register moved after the report was issued.
  const isStale = !!(
    issuance.issued &&
    integrity.issues_last_updated_at &&
    issuance.issued_at &&
    new Date(integrity.issues_last_updated_at) > new Date(issuance.issued_at)
  );

  return (
    <div style={s.overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={s.modal}>
        {/* ── Header ── */}
        <div style={s.header}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
              <h2 style={{ margin: 0, fontSize: 17, color: '#111827' }}>📄 Auditor's Report</h2>
              {header.entity && (
                <span style={{ fontSize: 12, color: '#6b7280' }}>
                  {header.entity}{header.period ? ` — ${header.period}` : ''}
                </span>
              )}
              {header.report_id && <span style={s.badge('teal')}>{header.report_id}</span>}
              {issuance.issued
                ? <span style={s.badge('green')}>Issued {fmtDay(issuance.issued_at)}</span>
                : <span style={s.badge('yellow')}>Draft</span>}
              {opinion.effective_type && (
                <span style={s.badge(
                  opinion.effective_type === 'unmodified' ? 'green'
                  : opinion.effective_type === 'qualified' ? 'yellow'
                  : 'red'
                )}>
                  {OPINION_TYPE_LABELS[opinion.effective_type] || opinion.effective_type}
                  {!opinion.effective_type_confirmed && ' (draft)'}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', marginTop: 10 }}>
              <button style={s.tab(tab === 'report')} onClick={() => setTab('report')}>📄 Report</button>
              <button style={s.tab(tab === 'register')} onClick={() => setTab('register')}>
                ⚠️ Issues Register {summary.total ? `(${summary.open}/${summary.total} open)` : ''}
              </button>
              <button style={s.tab(tab === 'controls')} onClick={() => setTab('controls')}>🛡 Internal Control</button>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', paddingBottom: 10 }}>
            <button style={s.refreshBtn} onClick={() => load(true)} disabled={loading} title="Rebuild from the current issues register">
              {loading ? '…' : '↻ Rebuild'}
            </button>
            <button style={s.refreshBtn} onClick={() => report && printReport(report)} disabled={!report} title="Open a printable version">
              🖨 Print
            </button>
            <button style={s.closeBtn} onClick={onClose}>✕</button>
          </div>
        </div>

        <div style={s.body}>
          {loading && (
            <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
              <div style={{ fontSize: 22 }}>⚙️</div>
              <div style={{ marginTop: 6 }}>Reading the issues register…</div>
            </div>
          )}

          {!loading && error && (
            <div style={s.banner('red')}>
              {error}
              <div style={{ marginTop: 8 }}>
                <button style={s.actionBtn('#991b1b')} onClick={() => load(true)}>Try again</button>
              </div>
            </div>
          )}

          {!loading && !error && report && (
            <>
              {isStale && (
                <div style={s.banner('yellow')}>
                  <strong>The register changed after this report was issued.</strong> An issue was updated on{' '}
                  {fmtDate(integrity.issues_last_updated_at)}, after the report was issued on {fmtDate(issuance.issued_at)}.
                  Rebuild and reissue so the report matches the register.
                </div>
              )}

              {/* ── REPORT TAB ── */}
              {tab === 'report' && (
                <>
                  <div style={s.summaryGrid}>
                    {[
                      { label: 'Issues raised', value: summary.total ?? 0, color: '#111827' },
                      { label: 'Open', value: summary.open ?? 0, color: summary.open > 0 ? '#dc2626' : '#059669' },
                      { label: 'Closed', value: summary.closed ?? 0, color: '#059669' },
                      { label: 'Material weaknesses', value: summary.open_material_weaknesses ?? 0, color: summary.open_material_weaknesses > 0 ? '#991b1b' : '#059669' },
                      { label: 'Significant deficiencies', value: summary.open_significant_deficiencies ?? 0, color: summary.open_significant_deficiencies > 0 ? '#b45309' : '#059669' },
                      { label: 'Overdue', value: summary.overdue ?? 0, color: summary.overdue > 0 ? '#b45309' : '#059669' },
                      { label: 'Awaiting verification', value: summary.awaiting_verification ?? 0, color: summary.awaiting_verification > 0 ? '#2563eb' : '#059669' },
                      { label: 'Unremediated', value: summary.unremediated_amount_display ?? '$0', color: summary.unremediated_amount > 0 ? '#dc2626' : '#059669' },
                    ].map(t => (
                      <div key={t.label} style={s.summaryTile}>
                        <div style={{ fontSize: 19, fontWeight: 700, color: t.color }}>{t.value}</div>
                        <div style={{ fontSize: 10, color: '#6b7280', marginTop: 2 }}>{t.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Opinion assessment */}
                  <div style={s.banner(opinion.recommendation === 'modification_consideration_required' ? 'red' : opinion.recommendation === 'unmodified' ? 'teal' : 'yellow')}>
                    <div style={{ fontWeight: 700, marginBottom: 4 }}>
                      Opinion assessment: {RECOMMENDATION_LABELS[opinion.recommendation] || opinion.recommendation}
                    </div>
                    {opinion.carried_opinion && (
                      <div style={{ marginBottom: 6 }}>
                        Opinion recorded at the findings step: <strong>{String(opinion.carried_opinion)}</strong>
                      </div>
                    )}
                    {(opinion.modifiers || []).map((m, i) => (
                      <div key={i} style={{ marginTop: 5, paddingLeft: 10, borderLeft: '2px solid currentColor' }}>
                        <strong>{m.count} × {m.condition.replace(/_/g, ' ')}</strong> — {m.effect}
                      </div>
                    ))}
                    <div style={{ marginTop: 8, fontSize: 11, opacity: 0.85 }}>{opinion.note}</div>
                  </div>

                  {/* Narrative sections — AR-010..090 is the AU-C 700 opinion
                      report, AR-100+ is the schedule of findings. The divider
                      marks where one ends and the other begins, matching the
                      page-break in the printable version. */}
                  {(report.narrative_sections || []).map(sec => (
                    <React.Fragment key={sec.section_id}>
                      {sec.section_id === 'AR-100' && (
                        <div style={{
                          margin: '22px 0 14px', paddingTop: 14, borderTop: '2px solid #111827',
                          fontSize: 11, color: '#6b7280', fontStyle: 'italic', textAlign: 'center',
                          textTransform: 'uppercase', letterSpacing: 0.6,
                        }}>
                          Report on Internal Control and Compliance follows
                        </div>
                      )}
                      <div style={s.sectionCard}>
                        <div style={s.sectionHeader}>
                          <span style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>
                            <span style={{ color: '#0f766e', marginRight: 8 }}>{sec.section_id}</span>
                            {sec.title}
                          </span>
                        </div>
                        <div style={s.sectionBody}>
                          <pre style={{ margin: 0, fontFamily: 'inherit', fontSize: 12, whiteSpace: 'pre-wrap', lineHeight: 1.65, color: '#374151' }}>
                            {sec.body}
                          </pre>
                        </div>
                      </div>
                    </React.Fragment>
                  ))}

                  {/* Issue the report */}
                  <div style={{ marginTop: 16, padding: 14, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
                    {issuance.issued ? (
                      <div style={{ fontSize: 12, color: '#374151' }}>
                        <strong>Issued {fmtDate(issuance.issued_at)}</strong> by {issuance.issued_by?.name || 'unknown'}
                        {issuance.opinion_confirmed ? ` · Opinion: ${issuance.opinion_confirmed}` : ''}
                        <div style={{ color: '#6b7280', marginTop: 3 }}>
                          {issuance.open_count_at_issuance} of {issuance.issue_count_at_issuance} issues were open at issuance.
                          {issuance.acknowledged_unverified && ' Issued with verification outstanding.'}
                        </div>
                        {issuance.issuance_note && <div style={{ marginTop: 6 }}>{issuance.issuance_note}</div>}
                      </div>
                    ) : showIssueForm ? (
                      <>
                        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6, color: '#111827' }}>Issue this report</div>
                        <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 8 }}>
                          Issuing rebuilds the report one more time and locks the snapshot with your name and the issue count at that moment.
                        </div>
                        <div style={{ marginBottom: 8 }}>
                          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#111827', marginBottom: 4 }}>
                            Opinion (AU-C 700)
                          </label>
                          <select
                            style={s.select}
                            value={issueOpinion}
                            onChange={e => setIssueOpinion(e.target.value)}
                          >
                            <option value="">Select the opinion you are issuing…</option>
                            {Object.entries(OPINION_TYPE_LABELS).map(([v, l]) => (
                              <option key={v} value={v}>{l}</option>
                            ))}
                          </select>
                          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                            {opinion.recommendation === 'modification_consideration_required'
                              ? 'The register contains conditions that may require a modified opinion. Confirm your determination before issuing.'
                              : 'This is your determination as engagement partner, not the system\u2019s. It sets the opinion paragraph in the issued report.'}
                          </div>
                        </div>
                        <textarea
                          style={s.textarea}
                          value={issueNote}
                          onChange={e => setIssueNote(e.target.value)}
                          placeholder="Issuance note (optional)"
                        />
                        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                          <button
                            style={{ ...s.actionBtn('#0f766e'), opacity: issueOpinion ? 1 : 0.5, cursor: issueOpinion ? 'pointer' : 'not-allowed' }}
                            onClick={() => submitIssue(false)}
                            disabled={issuing || !issueOpinion}
                            title={issueOpinion ? '' : 'Select an opinion before issuing'}
                          >
                            {issuing ? 'Issuing…' : 'Issue report'}
                          </button>
                          <button style={s.ghostBtn} onClick={() => setShowIssueForm(false)}>Cancel</button>
                        </div>
                      </>
                    ) : (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                        <span style={{ fontSize: 12, color: '#6b7280' }}>
                          Draft. Built from the register at {fmtDate(header.generated_at)}.
                        </span>
                        <button style={s.actionBtn('#0f766e')} onClick={() => setShowIssueForm(true)}>Issue report</button>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* ── REGISTER TAB ── */}
              {tab === 'register' && (
                <>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                    <select style={s.select} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                      <option value="">All statuses</option>
                      <option value="open">Open (any stage)</option>
                      <option value="overdue">Overdue</option>
                      <option value="in_remediation">In remediation</option>
                      <option value="pending_review">Pending auditor review</option>
                      <option value="escalated">Escalated</option>
                      <option value="closed">Closed</option>
                    </select>
                    <select style={s.select} value={filterType} onChange={e => setFilterType(e.target.value)}>
                      <option value="">All types</option>
                      {Object.entries(ISSUE_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                    <span style={{ fontSize: 11, color: '#6b7280' }}>
                      Showing {filtered.length} of {report.issues_register?.length || 0}
                    </span>
                    <span style={{ marginLeft: 'auto', fontSize: 11, color: '#6b7280' }}>
                      Register last changed {fmtDate(integrity.issues_last_updated_at)}
                    </span>
                  </div>

                  {filtered.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 30, color: '#9ca3af', fontSize: 13 }}>
                      No issues match this filter. Raise issues from the Workpapers modal.
                    </div>
                  ) : (
                    <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            <th style={s.th}>#</th>
                            <th style={s.th}>Issue</th>
                            <th style={s.th}>Type</th>
                            <th style={s.th}>Severity</th>
                            <th style={s.th}>Status</th>
                            <th style={s.th}>Stage</th>
                            <th style={s.th}>Verification</th>
                            <th style={s.th}>Due</th>
                            <th style={{ ...s.th, textAlign: 'right' }}>Amount</th>
                            <th style={s.th} />
                          </tr>
                        </thead>
                        <tbody>
                          {filtered.map(row => <RegisterRow key={row.issue_id} row={row} />)}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}

              {/* ── INTERNAL CONTROL TAB ── */}
              {tab === 'controls' && (
                <>
                  {[
                    { key: 'material_weaknesses', title: 'Material Weaknesses', tone: 'red' },
                    { key: 'significant_deficiencies', title: 'Significant Deficiencies', tone: 'yellow' },
                    { key: 'compliance_violations', title: 'Compliance Violations', tone: 'red' },
                    { key: 'other_deficiencies', title: 'Other Deficiencies and Observations', tone: 'grey' },
                  ].map(bucket => {
                    const items = controls[bucket.key] || [];
                    return (
                      <div key={bucket.key} style={{ marginBottom: 18 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#111827', marginBottom: 8 }}>
                          {bucket.title} ({items.length})
                          {items.filter(i => i.is_open).length > 0 && (
                            <span style={{ ...s.badge(bucket.tone === 'grey' ? 'grey' : bucket.tone), marginLeft: 8 }}>
                              {items.filter(i => i.is_open).length} open
                            </span>
                          )}
                        </div>
                        {items.length === 0 ? (
                          <div style={{ fontSize: 12, color: '#9ca3af', padding: '8px 0' }}>None identified.</div>
                        ) : items.map(i => (
                          <div key={i.issue_id} style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: 10, marginBottom: 7 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                              <span style={{ fontSize: 12, fontWeight: 600, color: '#111827' }}>
                                #{i.issue_number} {i.title}
                              </span>
                              <span style={{
                                ...s.badge('grey'),
                                background: `${STATUS_COLORS[i.status]}18`,
                                border: `1px solid ${STATUS_COLORS[i.status]}40`,
                                color: STATUS_COLORS[i.status],
                                flexShrink: 0,
                              }}>
                                {i.status_label}
                              </span>
                            </div>
                            {i.description && (
                              <div style={{ fontSize: 11, color: '#4b5563', marginTop: 5, lineHeight: 1.6 }}>{i.description}</div>
                            )}
                            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 6, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                              <StagePips stage={i.remediation_stage} />
                              <span>{i.remediation_percent}% remediated</span>
                              <span>Verification: {VERIFICATION_LABELS[i.verification_status]}</span>
                              {i.workpaper_ref && <span>WP {i.workpaper_ref}</span>}
                              {i.amount_display && <span>{i.amount_display}</span>}
                              {i.is_overdue && <span style={{ color: '#b45309', fontWeight: 600 }}>{i.days_overdue}d overdue</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </>
              )}

              <div style={{ marginTop: 18, padding: '10px 14px', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 11, color: '#6b7280' }}>
                Built from the live issues register · {integrity.issue_count_at_build} issues read at {fmtDate(header.generated_at)} ·
                Generated by RPA Automated Audit System (Azure Government)
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// EXPORTED BUTTON
// ─────────────────────────────────────────────────────────────────────────────
const GenerateAuditorsReportButton = ({ docId, onDone, disabledHint }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        style={{ ...s.triggerBtn, ...(disabledHint ? { background: '#9ca3af', cursor: 'not-allowed' } : {}) }}
        onClick={() => !disabledHint && setOpen(true)}
        disabled={!!disabledHint}
        title={disabledHint || "Auditor's report with the current issues register"}
      >
        📄 Auditor's Report
      </button>
      {open && (
        <AuditorsReportModal
          docId={docId}
          onClose={() => setOpen(false)}
          onIssued={onDone}
        />
      )}
    </>
  );
};

export default GenerateAuditorsReportButton;